import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Avatar, Botao, Cartao, Esqueleto, TituloSecao } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import type { Perfil, Turma } from '@/tipos/tabelas'
import { CHAVES_ADMIN, useTurmasAdmin } from './comum'

interface PerfilComMatriculas extends Perfil {
  matriculas: {
    id: string
    status: string
    data_inicio: string
    turma: { id: string; nome: string } | null
  }[]
}

/**
 * Participantes e matrículas.
 *
 * A conta de cada participante nasce no primeiro login dela, não aqui:
 * criar usuário pelo painel exigiria a service_role no navegador, o que
 * daria acesso irrestrito ao banco a quem abrisse o DevTools. O fluxo é
 * ela entrar com o e-mail, a conta aparecer nesta lista, e a Raquel
 * matricular.
 */
export function SecaoParticipantes() {
  const clienteQuery = useQueryClient()

  // Consulta única de turmas, compartilhada com o resto da administração
  // (ver comum.ts) — aqui só nos interessam as ativas.
  const { data: todasAsTurmas = [] } = useTurmasAdmin()
  const turmas: Turma[] = todasAsTurmas.filter((t) => t.ativa)

  const {
    data: pessoas = [],
    isLoading,
    error: erroLista,
  } = useQuery({
    queryKey: CHAVES_ADMIN.participantes,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('perfis')
        .select('*, matriculas(id, status, data_inicio, turma:turmas(id, nome))')
        .order('criado_em', { ascending: false })

      if (error) throw error
      return data as PerfilComMatriculas[]
    },
  })

  const matricular = useMutation({
    mutationFn: async ({ userId, turmaId }: { userId: string; turmaId: string }) => {
      const turma = turmas.find((t) => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada. Atualize a página e tente de novo.')

      const { error } = await supabase.from('matriculas').insert({
        user_id: userId,
        turma_id: turma.id,
        // A contagem do Dia 1 parte daqui. Quem entra depois do começo
        // usa a data da turma mesmo, para acompanhar o grupo.
        data_inicio: turma.data_inicio,
      })
      if (error) throw error
    },
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.participantes })
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas })
    },
  })

  const mover = useMutation({
    mutationFn: async ({ matriculaId, turmaId }: { matriculaId: string; turmaId: string }) => {
      const turma = turmas.find((t) => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')

      // A data de início muda junto — sem isso o Dia X dela continuaria
      // contado a partir da turma antiga, deslocado da turma nova.
      const { error } = await supabase
        .from('matriculas')
        .update({ turma_id: turma.id, data_inicio: turma.data_inicio })
        .eq('id', matriculaId)
      if (error) throw error
    },
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.participantes })
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.turmas })
    },
  })

  const semTurma = pessoas.filter(
    (p) => p.papel === 'participante' && p.matriculas.length === 0,
  )
  const comTurma = pessoas.filter((p) => p.matriculas.length > 0)

  return (
    <div className="flex flex-col gap-6">
      <Cartao className="bg-dourado/15">
        <h3 className="mb-1 font-titulo text-sm font-semibold">Como matricular alguém</h3>
        <p className="text-sm leading-relaxed text-[var(--tinta-suave)]">
          Peça para a participante entrar no app com o e-mail dela. A conta é criada nesse momento e
          ela aparece aqui como <strong>sem turma</strong>. Aí é só matricular.
        </p>
      </Cartao>

      {erroLista && (
        <Cartao className="border-2 border-pink bg-pink/10">
          <p className="font-titulo text-sm font-semibold text-pink">
            Não consegui carregar a lista de participantes
          </p>
          <p className="mt-1 text-sm text-[var(--tinta-suave)]">
            {mensagemDeErro(erroLista)}
          </p>
        </Cartao>
      )}

      <section>
        <TituloSecao
          acao={
            semTurma.length > 0 ? (
              <span className="font-titulo text-sm font-semibold text-pink">
                {semTurma.length} aguardando
              </span>
            ) : undefined
          }
        >
          Sem turma
        </TituloSecao>

        {isLoading ? (
          <Esqueleto className="h-24" />
        ) : semTurma.length === 0 ? (
          <Cartao className="text-sm text-[var(--tinta-apagada)]">
            {erroLista ? 'Corrija o erro acima para ver a lista.' : 'Ninguém aguardando matrícula.'}
          </Cartao>
        ) : (
          <div className="flex flex-col gap-2">
            {semTurma.map((pessoa) => (
              <LinhaSemTurma
                key={pessoa.id}
                pessoa={pessoa}
                turmas={turmas}
                matriculando={matricular.isPending && matricular.variables?.userId === pessoa.id}
                erro={
                  matricular.isError && matricular.variables?.userId === pessoa.id
                    ? matricular.error instanceof Error
                      ? matricular.error.message
                      : String(matricular.error)
                    : null
                }
                aoMatricular={(turmaId) => matricular.mutate({ userId: pessoa.id, turmaId })}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <TituloSecao>Matriculadas</TituloSecao>

        {comTurma.length === 0 ? (
          <Cartao className="text-sm text-[var(--tinta-apagada)]">Ninguém matriculado ainda.</Cartao>
        ) : (
          <div className="flex flex-col gap-2">
            {comTurma.map((pessoa) => {
              const matricula = pessoa.matriculas[0]
              return (
                <Cartao key={pessoa.id} className="flex flex-col gap-3 p-4">
                  <div className="flex items-center gap-3">
                    <Avatar nome={pessoa.nome} email={pessoa.email} />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 font-titulo text-[15px] font-semibold">
                        <span className="truncate">{nomeParaExibir(pessoa)}</span>
                        {pessoa.papel === 'admin' && (
                          <span className="shrink-0 rounded-full bg-coral/15 px-2 py-0.5 text-[11px] font-medium text-coral">
                            admin
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-[var(--tinta-apagada)]">
                        {matricula?.turma?.nome ?? 'turma removida'} · início em{' '}
                        {matricula ? formatarData(matricula.data_inicio) : '—'}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-3 py-1 text-xs font-medium',
                        matricula?.status === 'ativa'
                          ? 'bg-verde/15 text-verde'
                          : 'bg-[var(--borda)] text-[var(--tinta-apagada)]',
                      )}
                    >
                      {matricula?.status ?? '—'}
                    </span>
                  </div>

                  {matricula && turmas.length > 1 && (
                    <label className="flex items-center gap-2 pl-13 text-xs text-[var(--tinta-apagada)]">
                      Mudar de turma:
                      <select
                        value={matricula.turma?.id ?? ''}
                        onChange={(e) =>
                          mover.mutate({ matriculaId: matricula.id, turmaId: e.target.value })
                        }
                        disabled={mover.isPending}
                        className="h-9 flex-1 rounded-full border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 text-xs font-medium text-[var(--tinta)]"
                      >
                        {turmas.map((turma) => (
                          <option key={turma.id} value={turma.id}>
                            {turma.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </Cartao>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------

/**
 * Uma pessoa aguardando matrícula, com a escolha de turma embutida na
 * própria linha.
 *
 * Antes a turma era escolhida numa seção à parte, no topo da página, e
 * o botão Matricular de cada pessoa ficava desabilitado até alguém
 * lembrar de rolar até lá e escolher — sem nenhuma pista de por quê.
 * Um <button disabled> não dispara onClick, então o clique não fazia
 * literalmente nada. Com a turma junto do botão que a usa, ela já vem
 * pré-selecionada sempre que existe pelo menos uma turma ativa, que é o
 * caso mais comum — uma cohort tem uma turma só.
 */
function LinhaSemTurma({
  pessoa,
  turmas,
  matriculando,
  erro,
  aoMatricular,
}: {
  pessoa: PerfilComMatriculas
  turmas: Turma[]
  matriculando: boolean
  erro: string | null
  aoMatricular: (turmaId: string) => void
}) {
  const [turmaId, setTurmaId] = useState(turmas[0]?.id ?? '')

  return (
    <Cartao className="border-l-4 border-l-dourado p-4">
      <div className="flex items-center gap-3">
        <Avatar nome={pessoa.nome} email={pessoa.email} />

        <div className="min-w-0 flex-1">
          <p className="truncate font-titulo text-[15px] font-semibold">{nomeParaExibir(pessoa)}</p>
          <p className="truncate text-xs text-[var(--tinta-apagada)]">
            Entrou em {formatarData(pessoa.criado_em)}
          </p>
        </div>

        {turmas.length > 1 && (
          <select
            value={turmaId}
            onChange={(e) => setTurmaId(e.target.value)}
            aria-label={`Turma para matricular ${nomeParaExibir(pessoa)}`}
            className="h-10 shrink-0 rounded-full border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 text-sm font-medium"
          >
            {turmas.map((turma) => (
              <option key={turma.id} value={turma.id}>
                {turma.nome}
              </option>
            ))}
          </select>
        )}

        <Botao
          tamanho="pequeno"
          disabled={!turmaId}
          carregando={matriculando}
          onClick={() => aoMatricular(turmaId)}
        >
          {turmas.length === 1 ? `Matricular em ${turmas[0]?.nome}` : 'Matricular'}
        </Botao>
      </div>

      {!turmaId && (
        <p className="mt-3 text-xs text-pink">
          Nenhuma turma ativa. Crie uma na aba <strong>Turmas</strong> para poder matricular.
        </p>
      )}

      {erro && (
        <p role="alert" className="mt-3 text-xs text-pink">
          {erro}
        </p>
      )}
    </Cartao>
  )
}

/** Nome se ela já preencheu; e-mail como identificação enquanto não preenche. */
function nomeParaExibir(pessoa: Perfil) {
  return pessoa.nome || pessoa.email || 'Sem nome'
}
